import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ButtonLoader from "../ButtonLoader";
import { useTranslation } from "react-i18next";
import DatePicker from "../mui/DatePicker";
import * as api from "../../services/api";
import { formData, FormErrorList, useFormAction } from "../../form";
import { useRevalidator } from "react-router";
import AutocompleteSelect from "../AutocompleteSelect";
import { useCurrentLocation } from "../LocationSelector";
import { companyTagGroup, insurance } from "shared/src/client.mjs";
import { compose, updateIn, updateInIfExists } from "shared/src/util.mjs";
import * as f from "../../utils/formatter";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../context/AuthenticationTokenContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";

const level1Roles = new Set([
  role.Front_Office,
  role.Back_Office,
  role.Floor_Manager,
  role.Manager_Customer_Care,
  role.Manager_Clinic,
]);
const level2Roles = new Set([role.Floor_Manager, role.Manager_Customer_Care]);

function useClient(id) {
  const [data, setData] = React.useState({
    client: null,
    loading: true,
  });
  const location_id = useCurrentLocation();

  React.useEffect(() => {
    (async () => {
      try {
        const d = await api.loadClientEditData({
          id,
          location_id,
          coordinatorFilter: {
            roles: {
              location_id: location_id,
              role: role.Treatment_Coordinator,
            },
          },
          doctorFilter: {
            roles: {
              location_id: location_id,
              role: role.Doctor,
            },
          },
        });
        const company = d.data.company;

        setData({
          loading: false,
          client: d.data.client,
          locations: company?.locations ?? [],
          importantInfoTags: company?.importantInfoTags ?? [],
          languageInfoTags: company?.languageInfoTags ?? [],
          coordinators: d.data.coordinators.data.map((c) => ({
            ...c,
            name: f.fullName(c),
          })),
          doctors: [
            ...d.data.doctors.data.map((d) => ({
              ...d,
              name: f.fullName(d),
            })),
          ],
        });
      } catch {
        setData((data) => ({ ...data, loading: false }));
      }
    })();
  }, [id]);

  return data;
}

function prefsFormConverter(prefs) {
  const res = {};
  for (const k in prefs) {
    const v = prefs[k];
    if (typeof v === "string") {
      const s = v.trim();
      if (s.length > 0) {
        res[k] = s;
      }
    } else {
      res[k] = v;
    }
  }

  return res;
}

function nullIfEmpty(...fields) {
  return (m) =>
    fields.reduce(
      (m, k) => updateInIfExists(m, [k], (v) => (v.length === 0 ? null : v)),
      m,
    );
}

function formConverter(client, data) {
  return compose(
    nullIfEmpty(
      "insurance_code",
      "city",
      "street",
      "post_code",
      "correspondence_city",
      "correspondence_street",
      "correspondence_post_code",
      "coordinator_id",
      "birthdate",
    ),
    (m) => updateInIfExists(m, ["companyTags"], (v) => Object.values(v)),
    (m) =>
      updateInIfExists(m, ["prefs"], (v) =>
        prefsFormConverter({ ...client?.prefs, ...v }),
      ),
    (m) => updateIn(m, ["doctors"], (v) => v ?? []),
  )(data);
}

function SectionTitle({ children }) {
  return (
    <Typography variant="h3" gutterBottom>
      {children}
    </Typography>
  );
}

function SectionContent({ children }) {
  return (
    <Box display="flex" flexDirection="column" gap={1} marginBottom={2}>
      {children}
    </Box>
  );
}

function Content({ data }) {
  const { t } = useTranslation();
  const roles = useRoles();
  const hasLevel1 = isGrantedAny(roles, level1Roles);
  const hasLevel2 = isGrantedAny(roles, level2Roles);

  const {
    client,
    locations,
    coordinators,
    doctors,
    importantInfoTags,
    languageInfoTags,
  } = data;

  const [anyDoctor, setAnyDoctor] = React.useState(client.any_doctor ?? false);

  const insuranceOptions = Object.entries(insurance).map(([code, label]) => ({
    id: code,
    name: label,
  }));

  const {
    defaultImportantInfoTags,
    defaultLanguageInfoTags,
    defaultCoordinator,
    defaultDoctors,
    defaultDifferentCorrespondenceAddress,
  } = React.useMemo(() => {
    const iit = new Set(client.importantInfoTags.map((t) => t.id) ?? []);
    const lit = new Set(client.languageInfoTags.map((t) => t.id) ?? []);
    const coord = client.coordinator_id;
    const doc = new Set(client.doctors.map((d) => d.id));

    return {
      defaultImportantInfoTags: importantInfoTags.filter((t) => iit.has(t.id)),
      defaultLanguageInfoTags: languageInfoTags.filter((t) => lit.has(t.id)),
      defaultCoordinator: coordinators?.find((c) => c.id === coord),
      defaultDoctors: doctors?.filter((d) => doc.has(d.id)) ?? [],
      defaultDifferentCorrespondenceAddress: [
        client.correspondence_city,
        client.correspondence_street,
        client.correspondence_post_code,
      ].some((v) => v != null),
    };
  }, [client, importantInfoTags, languageInfoTags, coordinators, doctors]);

  const [differentCorrespondenceAddress, setDifferentCorrespondenceAddress] =
    React.useState(defaultDifferentCorrespondenceAddress);

  return (
    <>
      <Grid container spacing={4}>
        <Grid size={4}>
          <SectionTitle>Základní info</SectionTitle>
          <SectionContent>
            <input type="hidden" name="id" value={client.id} />
            <TextField
              name="first_name"
              label="Jméno"
              defaultValue={client.first_name}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
            <TextField
              name="last_name"
              label="Příjmení"
              defaultValue={client.last_name}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
            <TextField
              name="birth_number"
              label="Rodné číslo"
              defaultValue={client.birth_number}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
            <DatePicker
              name="birthdate"
              label="Datum narození"
              defaultValue={client.birthdate}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />

            <AutocompleteSelect
              name="insurance_code"
              label={t("entity.client.insurance_code")}
              options={insuranceOptions}
              defaultValue={insuranceOptions.find(
                (o) => o.id === client.insurance_code,
              )}
              readOnly={!hasLevel1}
            />
          </SectionContent>
        </Grid>

        <Grid size={4}>
          <SectionTitle>Zdravotní údaje</SectionTitle>
          <SectionContent>
            <TextField
              name="risks"
              label={t("entity.client.risks")}
              defaultValue={client.risks}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
              multiline
            />

            <AutocompleteSelect
              name="location_id"
              label="Klinika"
              options={locations}
              defaultValue={locations.find((o) => o.id === client.location_id)}
              readOnly={!hasLevel1}
            />
            <input
              type="hidden"
              name="companyTags.important_info.group"
              value={companyTagGroup.IMPORTANT_INFO}
            />
            <AutocompleteSelect
              name="companyTags.important_info.tags"
              label="Důležité info"
              options={importantInfoTags}
              defaultValue={defaultImportantInfoTags}
              multiple
              readOnly={!hasLevel1}
            />
          </SectionContent>
        </Grid>
        {hasLevel2 && (
          <Grid size={4}>
            <>
              <SectionTitle>Preference klienta</SectionTitle>
              <SectionContent>
                <TextField
                  name="prefs.problem"
                  label="Problémový klient"
                  defaultValue={client.prefs.problem ?? ""}
                  slotProps={{ input: { readOnly: !hasLevel2 } }}
                />
                <TextField
                  name="prefs.text_preference"
                  label="Textová preference"
                  defaultValue={client.prefs.text_preference ?? ""}
                  slotProps={{ input: { readOnly: !hasLevel2 } }}
                />
                <Typography variant="h4" sx={{ mt: 2 }}>
                  Lékař
                </Typography>
                <FormControlLabel
                  name="any_doctor"
                  control={
                    <Checkbox
                      checked={anyDoctor}
                      readOnly={!hasLevel2}
                      slotProps={{
                        input: {
                          onChange: (e) => {
                            if (
                              e.currentTarget.getAttribute("readonly") == null
                            ) {
                              setAnyDoctor(!anyDoctor);
                            }
                          },
                        },
                      }}
                    />
                  }
                  label="Libovolný"
                />
                {!anyDoctor && (
                  <AutocompleteSelect
                    name="doctors"
                    label="Lékař"
                    options={doctors}
                    defaultValue={defaultDoctors}
                    readOnly={!hasLevel2}
                    multiple
                  />
                )}
                <FormControlLabel
                  name="prefs.requires_czech_speaker"
                  control={
                    <Checkbox
                      defaultChecked={
                        client.prefs.requires_czech_speaker ?? false
                      }
                      readOnly={!hasLevel2}
                      slotProps={{
                        input: {
                          onChange: (e) => {
                            if (
                              e.currentTarget.getAttribute("readonly") != null
                            ) {
                              e.preventDefault();
                            }
                          },
                        },
                      }}
                    />
                  }
                  label="Česky mluvící personál"
                />
              </SectionContent>
            </>
          </Grid>
        )}
        <Grid size={4}>
          <SectionTitle>Adresa trvalého bydliště</SectionTitle>
          <SectionContent>
            <TextField
              name="street"
              label="Ulice"
              defaultValue={client.street}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
            <TextField
              name="city"
              label="Město"
              defaultValue={client.city}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
            <TextField
              name="post_code"
              label="PSČ"
              defaultValue={client.post_code}
              slotProps={{ input: { readOnly: !hasLevel1 } }}
            />
          </SectionContent>
          <Typography variant="h4" gutterBottom>
            Korespondenční adresa
          </Typography>
          <SectionContent>
            <FormControlLabel
              control={
                <Checkbox
                  readOnly={!hasLevel1}
                  checked={differentCorrespondenceAddress}
                  onChange={(e, checked) => {
                    if (e.currentTarget.getAttribute("readonly") == null) {
                      setDifferentCorrespondenceAddress(checked);
                    }
                  }}
                />
              }
              label="Jiná než adresa trvalého bydliště"
            />
            {differentCorrespondenceAddress ? (
              <>
                <TextField
                  name="correspondence_street"
                  label={t("entity.client.correspondence_street")}
                  defaultValue={client.correspondence_street}
                  slotProps={{ input: { readOnly: !hasLevel1 } }}
                />
                <TextField
                  name="correspondence_city"
                  label={t("entity.client.correspondence_city")}
                  defaultValue={client.correspondence_city}
                  slotProps={{ input: { readOnly: !hasLevel1 } }}
                />
                <TextField
                  name="correspondence_post_code"
                  label={t("entity.client.correspondence_post_code")}
                  defaultValue={client.correspondence_post_code}
                  slotProps={{ input: { readOnly: !hasLevel1 } }}
                />
              </>
            ) : (
              <>
                <input name="correspondence_city" type="hidden" value="" />
                <input name="correspondence_street" type="hidden" value="" />
                <input name="correspondence_post_code" type="hidden" value="" />
              </>
            )}
          </SectionContent>
        </Grid>
        <Grid size={4}>
          <SectionTitle>Jazyk</SectionTitle>
          <SectionContent>
            <TextField
              name="prefs.language"
              label="Jazyk klienta"
              defaultValue={client.prefs.language ?? ""}
              slotProps={{ input: { readOnly: true } }}
            />
            <input
              type="hidden"
              name="companyTags.languages.group"
              value={companyTagGroup.LANGUAGES}
            />
            <AutocompleteSelect
              name="companyTags.languages.tags"
              label="Další jazyky"
              options={languageInfoTags}
              defaultValue={defaultLanguageInfoTags}
              multiple
              readOnly={!hasLevel1}
            />
          </SectionContent>
        </Grid>
        <Grid size={4}>
          <SectionTitle>Další</SectionTitle>
          <SectionContent>
            <AutocompleteSelect
              name="coordinator_id"
              label="Koordinátor"
              options={coordinators}
              defaultValue={defaultCoordinator}
              readOnly={!hasLevel1}
            />
          </SectionContent>
        </Grid>
      </Grid>
    </>
  );
}

export default function EditDialog({ close, client_id }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const data = useClient(client_id);
  const { client, loading: loadingClient } = data;

  const {
    loading: loadingForm,
    errors,
    onSubmit,
  } = useFormAction(
    (e) => api.patchClient(formConverter(data?.client, formData(e.target))),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  const loading = loadingClient || loadingForm;

  return (
    <Dialog open={true} onClose={close} maxWidth="lg" scroll="paper">
      <DialogTitle>
        Upravit údaje
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={onSubmit} id="editForm">
          {client == null ? <CircularProgress /> : <Content data={data} />}
          <FormErrorList formErrors={errors} />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => close()}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          form="editForm"
        >
          {loading && <ButtonLoader />}
          {t("form.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
